<template>
  <div ref="root" class="home-scroll-controller">
    <slot />
  </div>
</template>
<script setup>
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import Cookies from 'js-cookie'
import useEmitter from '@composables/useEmitter'

gsap.registerPlugin(ScrollTrigger)

const root = ref(null)
const emitter = useEmitter()
const PIN_RELEASE_DISTANCE = 20000
const FLAVORITE_PIN_RELEASE_MULTIPLIER = 2

let ctx
let mountFrame
let isActive = false
let hasPlayedIntro = false
let isIntroPlaying = false
let hasAgeGatePassed = Boolean(Cookies.get('ofage'))
let pendingIntroPlay = null

function flushPendingIntroPlay() {
  hasAgeGatePassed = true

  if (!pendingIntroPlay) return

  const play = pendingIntroPlay
  pendingIntroPlay = null
  play()
}

function queueIntroPlay(play) {
  if (hasAgeGatePassed) {
    play()
    return
  }

  pendingIntroPlay = () => {
    if (!isActive || hasPlayedIntro) return

    ctx?.add(play)
  }
}

function getViewportHeight() {
  return window.visualViewport?.height ?? window.innerHeight
}

function warmImages(images) {
  images.forEach((image) => {
    if (image.dataset.homeScrollDecoded) return

    image.loading = 'eager'
    image.decoding = 'async'

    image.decode?.()
      .then(() => {
        image.dataset.homeScrollDecoded = 'true'
      })
      .catch(() => {})
  })
}

function getIntroBottleOriginOffset(index, centerIndex) {
  const distanceFromCenter = index - centerIndex

  return {
    yPercent: -145,
    rotation: distanceFromCenter * -2,
  }
}

function setIntroBottleFinalState(bottleImages) {
  gsap.set(bottleImages, {
    clearProps: 'transform,opacity,willChange',
  })
}

function setIntroBottleEntranceState(bottleImages) {
  const centerIndex = Math.floor(bottleImages.length / 2)

  gsap.set(bottleImages, {
    opacity: 0,
    transformOrigin: '50% 100%',
    willChange: 'transform, opacity',
  })

  bottleImages.forEach((image, index) => {
    gsap.set(image, getIntroBottleOriginOffset(index, centerIndex))
  })
}

function playIntroBottleEntrance(bottleImages) {
  if (isIntroPlaying || hasPlayedIntro) return

  isIntroPlaying = true

  const centerIndex = Math.floor(bottleImages.length / 2)
  const orderedBottles = bottleImages
    .map((image, index) => ({
      image,
      index,
      distance: Math.abs(index - centerIndex),
    }))
    .sort((a, b) => a.distance - b.distance || Math.abs(a.index - centerIndex) - Math.abs(b.index - centerIndex))

  setIntroBottleEntranceState(bottleImages)

  gsap.timeline({
    defaults: {
      ease: 'power4.out',
      duration: 0.74,
    },
    onComplete: () => {
      hasPlayedIntro = true
      isIntroPlaying = false
      setIntroBottleFinalState(bottleImages)
    },
  })
    .to(orderedBottles.map(({ image }) => image), {
      yPercent: 0,
      rotation: 0,
      opacity: 1,
      stagger: {
        each: 0.11,
        from: 'start',
      },
    }, 0.08)
}

function setCopyPopStart(copyItems) {
  if (!copyItems.length) return

  gsap.set(copyItems, {
    autoAlpha: 0,
    yPercent: (index) => (index === 0 ? 18 : 28),
    scale: (index) => (index === 0 ? .92 : 1),
    transformOrigin: '50% 70%',
    force3D: true,
    willChange: 'transform, opacity',
  })
}

function createCopyPopTimeline(copyItems, trigger, id) {
  if (!copyItems.length || !trigger) return

  gsap.timeline({
    scrollTrigger: {
      id,
      trigger,
      start: 'center bottom',
      toggleActions: 'play none none reverse',
      invalidateOnRefresh: true,
    },
    onStart: () => {
      gsap.set(copyItems, {
        willChange: 'transform, opacity',
      })
    },
    onComplete: () => {
      gsap.set(copyItems, {
        clearProps: 'willChange',
      })
    },
    onReverseComplete: () => {
      gsap.set(copyItems, {
        clearProps: 'willChange',
      })
    },
  })
    .to(copyItems, {
      autoAlpha: 1,
      yPercent: 0,
      scale: 1,
      duration: .58,
      ease: 'back.out(1.7)',
      stagger: .12,
    })
}

function buildScene() {
  if (!isActive || !root.value || isIntroPlaying) return

  const sceneRoot = root.value
  const introSection = sceneRoot.querySelector('.intro')
  const introBottleImages = gsap.utils.toArray(introSection?.querySelectorAll('.carousel .swiper-slide img') || [])
  const aboutSection = sceneRoot.querySelector('.about')
  const aboutContent = aboutSection?.querySelector('.content-wrapper .container')
  const aboutCopy = aboutContent?.querySelector('.copy')
  const aboutCopyItems = gsap.utils.toArray(aboutCopy?.querySelectorAll('h1, p, .btn') || [])
  const ugcSection = sceneRoot.querySelector('.ugc')
  const ugcGrid = ugcSection?.querySelector('.grid')
  const ugcItems = gsap.utils.toArray(ugcSection?.querySelectorAll('.grid .grid-item') || [])
  const socialSection = sceneRoot.querySelector('.social')
  const socialContent = socialSection?.querySelector('.container')
  const socialImages = gsap.utils.toArray(socialSection?.querySelectorAll('img') || [])
  const flavoriteSection = sceneRoot.querySelector('.flavorite')
  const flavoriteCopy = flavoriteSection?.querySelector('.content-section')
  const flavoriteCopyItems = gsap.utils.toArray(flavoriteSection?.querySelectorAll('h2.secondary, .content-section p, .content-section .btn') || [])
  const blogSection = sceneRoot.querySelector('.blog-section')
  const blogContent = blogSection?.querySelector('.container') || blogSection
  const blogCopy = blogSection?.querySelector('.copy')
  const blogCopyItems = gsap.utils.toArray(blogSection?.querySelectorAll('.copy h1, .copy p') || [])
  const blogCarousel = blogSection?.querySelector('.blog-carousel')
  const blogCardPhotos = gsap.utils.toArray(blogSection?.querySelectorAll('.blog-card__photo') || [])
  const blogImages = gsap.utils.toArray(blogSection?.querySelectorAll('.blog-card img') || [])
  const blogScene = blogSection?.closest('.water-scene') || blogSection
  const wtbSection = sceneRoot.querySelector('.wtb-container')
  const bottleCatchSection = sceneRoot.querySelector('.bottle-catch')
  const backgroundImage = sceneRoot.querySelector('.bg')
  const aboutWaterCopy = aboutSection?.querySelector('.water-copy')

  if (!introSection || !aboutContent || !ugcSection || !ugcGrid || !socialSection || !socialContent || !flavoriteSection || !blogContent || !blogScene || !wtbSection || !bottleCatchSection) {
    mountFrame = window.requestAnimationFrame(buildScene)
    return
  }

  ctx?.revert()
  ctx = gsap.context(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let viewportHeight = getViewportHeight()

    const body = document.body

    gsap.set(sceneRoot, {
      position: 'relative',
      display: 'block',
      width: '100%',
    })

    gsap.set([introSection, aboutSection, ugcSection, socialSection, flavoriteSection, blogScene, blogSection, blogContent, wtbSection, bottleCatchSection], {
      position: 'relative',
      width: '100%',
    })

    gsap.set(introSection, { zIndex: 1 })
    gsap.set(aboutSection, {
      zIndex: 2,
      marginTop: '',
    })
    gsap.set(socialSection, { zIndex: 3 })
    gsap.set(ugcSection, { zIndex: 4 })
    gsap.set(flavoriteSection, { zIndex: 5 })
    gsap.set(blogScene, {
      zIndex: 7,
      marginTop: () => Math.round(getViewportHeight() * FLAVORITE_PIN_RELEASE_MULTIPLIER),
    })
    gsap.set(blogContent, {
      zIndex: 6,
    })
    gsap.set(bottleCatchSection, {
      zIndex: 7,
    })
    gsap.set(wtbSection, {
      zIndex: 8,
    })

    if (prefersReducedMotion) {
      gsap.set([aboutContent, ...aboutCopyItems, ugcGrid, socialContent, flavoriteSection, ...flavoriteCopyItems, blogScene, blogContent, ...blogCopyItems, ...blogCardPhotos, ...ugcItems, ...introBottleImages], {
        clearProps: 'all',
      })
      sceneRoot.classList.remove('scroll-prep')
      return
    }

    gsap.set(socialContent, {
      scale: 3,
      opacity: 0,
      transformOrigin: '50% 50%',
      force3D: true,
      willChange: 'transform, opacity',
    })

    gsap.set(socialImages, {
      force3D: true,
      backfaceVisibility: 'hidden',
      willChange: 'transform',
    })

    gsap.set(flavoriteSection, {
      autoAlpha: 1,
      yPercent: 6,
      force3D: true,
      willChange: 'transform, opacity',
    })

    gsap.set(ugcItems, {
      opacity: 0,
      scaleX: 0.4,
      scaleY: 0.6,
      transformOrigin: '50% 50%',
      force3D: true,
      willChange: 'transform, opacity',
    })

    gsap.set([aboutContent, ugcGrid], {
      autoAlpha: 1,
      force3D: true,
      willChange: 'transform, opacity',
    })

    gsap.set(blogContent, {
      autoAlpha: 1,
      force3D: true,
      willChange: 'transform, opacity',
    })

    setCopyPopStart(aboutCopyItems)
    setCopyPopStart(flavoriteCopyItems)
    setCopyPopStart(blogCopyItems)

    if (blogCardPhotos.length) {
      const scrapbookRotations = [-11, 8, -7, 10, -9]

      gsap.set(blogCardPhotos, {
        autoAlpha: 0,
        xPercent: (index) => (index % 2 === 0 ? -5 : 5),
        yPercent: (index) => (index % 2 === 0 ? -42 : -54),
        rotation: (index) => scrapbookRotations[index % scrapbookRotations.length],
        scale: .82,
        transformOrigin: '50% 88%',
        force3D: true,
        willChange: 'transform, opacity',
      })
    }

    warmImages([...introBottleImages, ...socialImages, ...blogImages])

    if (hasPlayedIntro) {
      setIntroBottleFinalState(introBottleImages)
    } else {
      setIntroBottleEntranceState(introBottleImages)
      queueIntroPlay(() => playIntroBottleEntrance(introBottleImages))
    }

    sceneRoot.classList.remove('scroll-prep')

    const createPanelPin = (
      panel,
      id,
      pinSpacing = false,
      getEndDistance = () => PIN_RELEASE_DISTANCE,
      start = () => {
        const panelHeight = Math.max(panel.offsetHeight || 0, panel.scrollHeight || 0)
        return panelHeight < viewportHeight ? 'top top' : 'bottom bottom'
      },
    ) => {
      ScrollTrigger.create({
        id,
        trigger: panel,
        start,
        end: () => `+=${getEndDistance()}`,
        pin: true,
        pinSpacing,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        refreshPriority: 1,
        onRefreshInit: () => {
          viewportHeight = getViewportHeight()
        },
      })
    }

    createPanelPin(introSection, 'home-overlap-intro')
    createPanelPin(aboutSection, 'home-overlap-about')
    createPanelPin(socialSection, 'home-overlap-social')
    createPanelPin(
      flavoriteSection,
      'home-overlap-flavorite',
      false,
      () => PIN_RELEASE_DISTANCE,
      () => 'bottom bottom',
    )
    createPanelPin(blogSection, 'home-overlap-blog-content')
    createPanelPin(bottleCatchSection, 'home-overlap-bottle-catch')

    gsap.timeline({
      scrollTrigger: {
        id: 'home-about-fade',
        trigger: ugcSection,
        start: 'top bottom',
        end: 'top top',
        scrub: true,
        invalidateOnRefresh: true,
      },
    })
      .to(aboutContent, {
        autoAlpha: 0,
        yPercent: -12,
        ease: 'none',
      }, 0)
      .fromTo(ugcGrid, {
        yPercent: 8,
      }, {
        yPercent: 0,
        ease: 'none',
      }, 0)

    const backgroundTargets = [body, socialSection, aboutWaterCopy].filter(Boolean)
    const setApplyBackground = (shouldApply) => {
      backgroundTargets.forEach((target) => {
        target.classList.toggle('apply-bg', shouldApply)
      })
    }

    ScrollTrigger.create({
      id: 'home-ugc-apply-bg',
      trigger: ugcSection,
      start: 'top top-=100',
      end: 'bottom top',
      onEnter: () => setApplyBackground(true),
      onEnterBack: () => setApplyBackground(true),
      onLeaveBack: () => setApplyBackground(false),
    })

    if (backgroundImage) {
      ScrollTrigger.create({
        id: 'home-blog-bg-move',
        trigger: blogSection,
        start: 'top top',
        invalidateOnRefresh: true,
        onEnter() {
          backgroundImage.classList.add('move')
        },
        onEnterBack() {
          backgroundImage.classList.add('move')
        },
        onLeaveBack() {
          backgroundImage.classList.remove('move')
        },
      })
    }

    createCopyPopTimeline(aboutCopyItems, aboutCopy || aboutContent, 'home-about-copy-pop')
    createCopyPopTimeline(flavoriteCopyItems, flavoriteCopy || flavoriteSection, 'home-flavorite-copy-pop')
    createCopyPopTimeline(blogCopyItems, blogCopy || blogSection, 'home-blog-copy-pop')

    if (blogCardPhotos.length) {
      gsap.timeline({
        scrollTrigger: {
          id: 'home-blog-scrapbook-slap',
          trigger: blogCarousel || blogSection,
          start: 'center bottom',
          toggleActions: 'play none none reverse',
          invalidateOnRefresh: true,
        },
        onStart: () => {
          gsap.set(blogCardPhotos, {
            willChange: 'transform, opacity',
          })
        },
        onComplete: () => {
          gsap.set(blogCardPhotos, {
            clearProps: 'willChange',
          })
        },
        onReverseComplete: () => {
          gsap.set(blogCardPhotos, {
            clearProps: 'willChange',
          })
        },
      })
        .to(blogCardPhotos, {
          autoAlpha: 1,
          xPercent: 0,
          yPercent: 0,
          rotation: 0,
          scale: 1,
          duration: .68,
          ease: 'back.out(2.2)',
          stagger: {
            each: .12,
            from: 'center',
          },
        })
    }

    ugcItems.forEach((item, index) => {
      gsap.timeline({
        scrollTrigger: {
          id: `home-ugc-item-${index}`,
          trigger: item,
          start: 'top 84%',
          end: 'top 52%',
          scrub: true,
          invalidateOnRefresh: true,
        },
      }).to(item, {
        opacity: 1,
        scaleX: 1,
        scaleY: 1,
        ease: 'none',
      })
    })

    gsap.timeline({
      scrollTrigger: {
        id: 'home-social-scale',
        trigger: socialSection,
        start: 'top bottom',
        end: 'top top',
        scrub: true,
        invalidateOnRefresh: true,
      },
    })
      .to(ugcGrid, {
        autoAlpha: 0,
        yPercent: -5,
        ease: 'none',
      }, 0)
      .to(socialContent, {
        scale: 1,
        opacity: 1,
        ease: 'none',
      }, 0)

    gsap.timeline({
      scrollTrigger: {
        id: 'home-social-flavorite',
        trigger: flavoriteSection,
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: true,
        invalidateOnRefresh: true,
      },
    })
      .to(socialSection, {
        autoAlpha: 0,
        ease: 'none',
      }, 0)
      .to(flavoriteSection, {
        autoAlpha: 1,
        yPercent: 0,
        ease: 'none',
      }, 0)

    gsap.timeline({
      scrollTrigger: {
        id: 'home-blog-bottle-catch-fade',
        trigger: bottleCatchSection,
        start: 'top bottom',
        end: 'top top',
        scrub: true,
        invalidateOnRefresh: true,
      },
    })
      .to(blogContent, {
        autoAlpha: 0,
        yPercent: -6,
        ease: 'none',
      }, 0)
  }, sceneRoot)
}

onMounted(async () => {
  await nextTick()
  isActive = true
  hasAgeGatePassed = Boolean(Cookies.get('ofage'))
  emitter.on('ofage', flushPendingIntroPlay)

  mountFrame = requestAnimationFrame(buildScene)
})

onBeforeUnmount(() => {
  isActive = false
  isIntroPlaying = false

  window.cancelAnimationFrame(mountFrame)

  emitter.off('ofage', flushPendingIntroPlay)

  pendingIntroPlay = null
  ctx?.revert()
})
</script>
